import { type MouseEvent, useState } from 'react';
import { Link } from 'react-router';

import { LanguageBar, LoginState, useSelectLoginState } from '@treneo/features/auth';
import { Breadcrumbs } from '@treneo/features/breadcrumb';
import { SearchBar, useCourseTagsQuery } from '@treneo/features/courses';
import { NotificationBell } from '@treneo/features/notifications';
import { useTrainerInvitationNotifications } from '@treneo/features/organizations';
import { useSaveIsStartBannerHidden } from '@treneo/features/user';
import { urls } from '@treneo/shared/constants';
import { useAppTranslations } from '@treneo/shared/i18n';
import { AddIcon, IconSizes, PersonAddIcon, RocketIcon, SchoolIcon } from '@treneo/shared/icons';
import { useAppLocation, useAppNavigate } from '@treneo/shared/router';
import {
  DropdownMenu,
  type DropdownMenuItem,
  IconButton,
  Logo,
  LOGO_COLORS,
  LOGO_VARIANTS
} from '@treneo/shared/ui';

import { UserProfileModal } from '../user-profile-modal/user-profile-modal';

import { BaseNavbar } from './base-navbar';

type Props = {
  isForLogged: boolean;
  onCreateCourse?: () => void;
};

export const Navbar = ({ isForLogged, onCreateCourse }: Readonly<Props>) => {
  const { t } = useAppTranslations();
  const navigate = useAppNavigate();
  const location = useAppLocation();
  const { mutate: saveIsStartBannerHiddenMutation } = useSaveIsStartBannerHidden();
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const shouldShowSearchBar = isForLogged && location.pathname === '/';
  const loginState = useSelectLoginState();
  const { notifications, isLoading: areNotificationsLoading } =
    useTrainerInvitationNotifications(isForLogged);

  const { data: courseTagsData, isLoading: areTagsLoading } = useCourseTagsQuery();
  const availableTagsWithTranslations = courseTagsData?.tags ?? [];

  const handleAddMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleCreateCourse = () => {
    if (onCreateCourse) {
      onCreateCourse();
    } else {
      navigate(urls.createCoursePage);
    }
    handleAddMenuClose();
  };

  const handleShowStartBanner = () => {
    saveIsStartBannerHiddenMutation({
      isStartBannerHidden: false,
      isLogged: loginState === LoginState.LOGGED
    });
    navigate({ pathname: urls.homePage, search: '' }, { replace: true });
    handleAddMenuClose();
  };

  const menuItems: DropdownMenuItem[] = [
    {
      id: 'create-course',
      icon: <SchoolIcon fontSize={IconSizes.small} />,
      label: t('navbar.createCourse'),
      onClick: handleCreateCourse
    },
    {
      id: 'invite-friend',
      icon: <PersonAddIcon fontSize={IconSizes.small} />,
      label: t('navbar.inviteFriend'),
      onClick: () => {}
    },
    {
      id: 'show-start-banner',
      icon: <RocketIcon fontSize={IconSizes.small} />,
      label: t('navbar.whatIsTreneo'),
      onClick: handleShowStartBanner
    }
  ];

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate({ pathname: urls.homePage, search: '' }, { replace: true });
  };

  const leftContent = (
    <>
      <Link to={urls.homePage} onClick={handleLogoClick}>
        <Logo variant={LOGO_VARIANTS.menu} color={LOGO_COLORS.dark} />
      </Link>
      {isForLogged && <Breadcrumbs />}
    </>
  );

  const centerContent = shouldShowSearchBar ? (
    <>
      <SearchBar
        availableTags={availableTagsWithTranslations}
        areTagsLoading={areTagsLoading}
        placeholder={t('coursesPage.searchCourse')}
      />

      <IconButton
        onClick={handleAddMenuOpen}
        variant='primary'
        size='medium'
        aria-label={t('navbar.addMenu')}
      >
        <AddIcon />
      </IconButton>

      <DropdownMenu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        items={menuItems}
      />
    </>
  ) : null;

  const hasNotifications = notifications.length > 0;
  const shouldShowNotificationBell = isForLogged && (hasNotifications || areNotificationsLoading);

  const rightContent = (
    <>
      {shouldShowNotificationBell && (
        <NotificationBell notifications={notifications} isLoading={areNotificationsLoading} />
      )}
      {isForLogged && <UserProfileModal />}
      <LanguageBar />
    </>
  );

  return (
    <BaseNavbar
      leftContent={leftContent}
      centerContent={centerContent}
      rightContent={rightContent}
      variant='default'
    />
  );
};
